import { useState } from 'react';
import { useQuery } from 'react-query';
import { useDebounce } from 'react-use';

import {
  COMPANY_SEARCH_MINIMUM_QUERY_LENGTH,
  searchCompaniesQuery,
} from '@savgroup-front-common/core/api/Seller/searchCompaniesQuery';
import { CompanySuggestion } from '@savgroup-front-common/types/CompanyDirectory';

export const COMPANY_SEARCH_QUERY_KEY = 'searchCompanies';
const COMPANY_SEARCH_DEBOUNCE_IN_MS = 250;
const COMPANY_SEARCH_LIMIT = 8;

interface UseCompanySearchArgs {
  query: string;
  countryCode?: string;
}

interface UseCompanySearchReturnValues {
  suggestions: CompanySuggestion[];
  isSearching: boolean;
  hasRegisterFailed: boolean;
}

export function useCompanySearch({
  query,
  countryCode,
}: UseCompanySearchArgs): UseCompanySearchReturnValues {
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useDebounce(() => setDebouncedQuery(query), COMPANY_SEARCH_DEBOUNCE_IN_MS, [
    query,
  ]);

  const trimmedQuery = debouncedQuery.trim();
  const isQueryLongEnough =
    trimmedQuery.length >= COMPANY_SEARCH_MINIMUM_QUERY_LENGTH;

  const { data, isFetching } = useQuery(
    [COMPANY_SEARCH_QUERY_KEY, { trimmedQuery, countryCode }],
    async ({ signal }) => {
      const response = await searchCompaniesQuery({
        query: trimmedQuery,
        countryCode,
        limit: COMPANY_SEARCH_LIMIT,
        signal,
      });

      if (response.failure) {
        return { suggestions: [], hasRegisterFailed: true };
      }

      return { suggestions: response.value, hasRegisterFailed: false };
    },
    { enabled: isQueryLongEnough, keepPreviousData: true },
  );

  const isQueryPending =
    query.trim() !== trimmedQuery &&
    query.trim().length >= COMPANY_SEARCH_MINIMUM_QUERY_LENGTH;

  if (!isQueryLongEnough) {
    return {
      suggestions: [],
      isSearching: isQueryPending,
      hasRegisterFailed: false,
    };
  }

  return {
    suggestions: data?.suggestions ?? [],
    isSearching: isFetching || isQueryPending,
    hasRegisterFailed: data?.hasRegisterFailed ?? false,
  };
}
